import exifr from 'exifr'
import ExifReader from 'exifreader'

/**
 * 写真からGPS情報と撮影日時を抽出
 * iOS Safari対応: FileをArrayBufferに変換してから読み取る
 * フォールバック: exifr → ExifReader
 * @param {File} file - 画像ファイル
 * @returns {Promise<Object>} GPS情報、撮影日時などを含むオブジェクト
 */
export async function extractPhotoGpsData(file) {
  // iOS Safari workaround: FileをArrayBufferに変換
  let arrayBuffer
  try {
    arrayBuffer = await file.arrayBuffer()
  } catch {
    return {
      file,
      originalName: file.name,
      gps: null,
      timestamp: null,
      camera: null,
    }
  }

  // 方法1: exifrで試行
  let gps = null
  let exifData = null

  try {
    gps = await exifr.gps(arrayBuffer)
    exifData = await exifr.parse(arrayBuffer, {
      pick: ['DateTimeOriginal', 'Make', 'Model'],
    })
  } catch {
    // exifr失敗時はExifReaderでフォールバック
  }

  // 方法2: exifrでGPSが取れなかった場合、ExifReaderでフォールバック
  if (!gps) {
    try {
      const tags = await ExifReader.load(arrayBuffer, { expanded: true })

      if (tags.gps && tags.gps.Latitude !== undefined && tags.gps.Longitude !== undefined) {
        gps = {
          latitude: tags.gps.Latitude,
          longitude: tags.gps.Longitude,
          altitude: tags.gps.Altitude,
        }
      }

      // 撮影日時も取得
      if (!exifData && tags.exif) {
        exifData = {
          DateTimeOriginal: tags.exif.DateTimeOriginal?.description,
          Make: tags.exif.Make?.description,
          Model: tags.exif.Model?.description,
        }
      }
    } catch {
      // ExifReaderも失敗
    }
  }

  const hasGps = gps && gps.latitude !== undefined && gps.longitude !== undefined

  return {
    file,
    originalName: file.name,
    gps: hasGps
      ? {
          latitude: gps.latitude,
          longitude: gps.longitude,
          altitude: gps.altitude,
        }
      : null,
    timestamp: exifData?.DateTimeOriginal,
    camera: exifData?.Make && exifData?.Model ? `${exifData.Make} ${exifData.Model}` : null,
  }
}

/**
 * HEIC→JPEG変換（iPhone写真対応）
 * @param {File} file - HEICファイル
 * @returns {Promise<File>} JPEG形式のFile
 */
async function convertHEICtoJPEG(file) {
  try {
    // 方法1: createImageBitmapを試す
    try {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      })

      const canvas = document.createElement('canvas')
      canvas.width = bitmap.width
      canvas.height = bitmap.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(bitmap, 0, 0)

      return await new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const newName = file.name.replace(/\.heic$/i, '.jpg')
              resolve(new File([blob], newName, { type: 'image/jpeg' }))
            } else {
              reject(new Error('Blob変換失敗'))
            }
          },
          'image/jpeg',
          0.95
        )
      })
    } catch (bitmapError) {
      console.warn('createImageBitmap失敗、FileReader方式にフォールバック:', bitmapError)

      // 方法2: FileReader + Image + Canvasを使う
      return await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = img.width
            canvas.height = img.height
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)

            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const newName = file.name.replace(/\.heic$/i, '.jpg')
                  resolve(new File([blob], newName, { type: 'image/jpeg' }))
                } else {
                  reject(new Error('Blob変換失敗'))
                }
              },
              'image/jpeg',
              0.95
            )
          }
          img.onerror = reject
          img.src = e.target.result
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    }
  } catch (error) {
    console.error('HEIC変換エラー:', error)
    // 変換失敗時はHEICのまま返す（compressImageToTargetで処理される）
    return file
  }
}
